// The inline alarm editor (TOM-673): the panel an ALL-ALARMS row expands into, so the
// common edit — a threshold, a severity, when to watch — happens where the
// alarm is read instead of on another page.
//
// It is deliberately the COMMON-CASE editor. `pages/AlarmDetail.tsx` stays, and
// stays the target of the quiet "Open full editor" link in this panel's footer:
// unlock-to-custom, the detailed evaluation panel, this alarm's history and
// duplicate-for-a-sibling all live there and none of them belong in a row. A
// panel that silently did half of one of those would be worse than a panel that
// says where the whole of it is.
//
// What DOES belong in the row, and came back here from that page: the per-mode
// threshold split, the instance pin, and delete. Each of them is a thing an
// owner reaches for while reading the alarm rather than while studying it, and
// each was a page navigation away from a panel that could already save.
//
// WHAT IT OWNS, and what it borrows:
//
//   - It owns NO vocabulary and NO arithmetic. Every draft mutation goes
//     through `lib/customAlarmForm.ts` (custom alarms) or `lib/alarmDefsForm.ts`
//     (recipe alarms), the words come from `lib/alarmSentence.ts` /
//     `lib/watchSchedule.ts` / `lib/alarmDisplay.ts`, and the ordering decisions
//     are unit-tested in `lib/alarmInlineEditor.ts`. This file renders.
//   - The two arms never cross. `isCustomDefinition` decides
//     (`inlineEditorVariant`), so a `custom:` definition can never be handed the
//     recipe form even when this build cannot read its block back — it gets an
//     honest "edit this one in the full editor" note instead of half a form.
//   - RECIPE params broken out per mode get ONE BOX PER ENABLED MODE, never one
//     shared box: a single box over per-mode values would show the first mode's
//     number and save it over every other mode. Collapsing the split back is the
//     destructive direction, so the panel says which numbers it would drop
//     before the click (`recipeParamSlots` carries the sentence).
//
// THE SAVE is the shipped convention, not an optimistic one: `PATCH
// /v1/boats/:boatId/alerts/:alertId` with a fresh `Idempotency-Key` and a
// MINIMAL-DIFF body from the form libs' own `toPatchBody`. An EMPTY patch never
// leaves the browser — a no-op would still bump the boat's ruleset version — and
// closes the panel without a refresh. On 2xx the panel hands the delivery
// sentence (`describeDelivery`) to `onSaved` and closes: the boat is what has to
// run the change, the page owns the banner region, and a note rendered inside a
// panel that is about to unmount would flash and vanish.
//
// LIVE PROOFS: the trigger row comes from `lib/alarmLive.ts` `livePanel`,
// called with the CURRENT draft so it answers for what is on screen rather than
// what is stored. Each CONDITION row derives its own proof from the draft row's
// path — the same `observationText` words livePanel would use — and its
// in-force gate from the BASELINE definition at the row's original stored index
// (`InlineEdit.conditionKeys`). Deriving per row is what keeps a proof attached
// to its own clause: the stored clause list does not shrink when the draft's
// does, so any stored-vs-draft zip hands a removed row's reading to its
// neighbour. Verdicts are judged from the edited row through `clauseHolds`,
// because a chip that ignored the number being typed would be worse than no
// chip.
//
// THE BASELINE: once dirty, the draft stops following snapshot refetches — but
// the save must then diff against the definition the draft was SEEDED from
// (`InlineEdit.baseline`), never the freshest one. Diffing against a refreshed
// definition puts every remotely-changed, locally-untouched field into the
// patch with its stale value: a silent revert of someone else's save.
//
// The integration agent wires `observationFor` as
// `observationLookup({ live: liveSamples, catalog: catalogByPath })` — the exact
// expression `pages/Alarms.tsx` already builds for its value lines.

import type { VesselType } from '@neomaris/protocol';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../../api';
import { turnOffAlarm } from '../../../lib/alarmApi';
import {
  initFromDef,
  setBinding,
  setEnabledModes as setRecipeModes,
  setSeverity as setRecipeSeverity,
  setSplitByMode,
  setThreshold,
  contactStateClearable,
  isContactStateParam,
  toPatchBody as toRecipePatchBody,
  isEmptyPatch as isEmptyRecipePatch,
  validate as validateRecipe,
  hasErrors as recipeHasErrors,
  type AlarmDraft,
} from '../../../lib/alarmDefsForm';
import {
  alarmConditionsSetup,
  describeDelivery,
  describeRemoval,
  readCustomSpec,
  readWireTrigger,
  unitVocabulary,
  type AlarmDefView,
  type AlarmRemovalResponse,
  type AlarmSeverity,
  type ConfiguredAlarmView,
  type DeliveryState,
} from '../../../lib/alarmDisplay';
import {
  DELETE_KEEP_LABEL,
  DELETE_LABEL,
  DELETE_PROMPT,
  DURATION_UNIT,
  INLINE_DELETE_IDLE,
  INLINE_SAVE_IDLE,
  SPLIT_TOGGLE_LABEL,
  UNSAVED_GUARD_DISCARD,
  UNSAVED_GUARD_MESSAGE,
  UNSAVED_GUARD_STAY,
  applyInlineEdit,
  askToDelete,
  bindingSlot,
  cancelDelete,
  combinedVerdict,
  conditionKeysAfterRemove,
  deleteButtonLabel,
  deleteErrorText,
  deleteFailed,
  deleting,
  fullEditorPath,
  initialConditionKeys,
  inlineEditorVariant,
  inlineSaveError,
  inlineSaved,
  inlineSaving,
  inlineSyncCaption,
  isDeleteConfirming,
  isDeleting,
  isInertWatchPreset,
  isSaving,
  jointExplanation,
  jointLabel,
  recipeParamSlots,
  recipeSourcePath,
  recipePatchErrorMessage,
  saveButtonLabel,
  saveErrorField,
  saveErrorText,
  sentenceInputFromDraft,
  withdrawnAlarmNote,
  sentenceSegments,
  severityOptions,
  toggledLogic,
  watchSlotOptions,
  writeModeFor,
  type BindingSlot,
  type InlineDeleteState,
  type InlineEdit,
  type InlineSaveState,
  type SentenceSegment,
} from '../../../lib/alarmInlineEditor';
import {
  livePanel,
  observationReading,
  observationText,
  type LiveObservation,
} from '../../../lib/alarmLive';
import { findTemplate, useAlertTemplates } from '../../../lib/alertTemplates';
import { clauseChip, conditionOperatorLabel } from '../../../lib/customAlarmConditions';
import { errorContextFor, mapPatchError } from '../../../lib/customAlarmErrors';
import {
  clauseHolds,
  combinedWouldFire,
  conditionOperatorsFor,
  customValueUnits,
  initFromCustomDef,
  isEmptyPatch as isEmptyCustomPatch,
  removeCondition,
  setClearDelaySeconds,
  setClearpoint,
  setConditionLogic,
  setConditionOperator,
  setConditionValue,
  setEnabledModes as setCustomModes,
  setSetDelaySeconds,
  setSetpoint,
  setSeverity as setCustomSeverity,
  setSignal,
  setStaleSeconds,
  setTriggerClass,
  triggerLabel,
  toPatchBody as toCustomPatchBody,
  validate as validateCustom,
  hasErrors as customHasErrors,
  type ConditionOperator,
  type CustomAlarmDraft,
} from '../../../lib/customAlarmForm';
import {
  signalDisplayName,
  signalLabel,
  suggestedTriggerClasses,
  type Signal,
  type TriggerClass,
} from '../../../lib/signalCatalog';
import { useUnits } from '../../../lib/useUnits';
import { watchPresetModes, type WatchPresetId } from '../../../lib/watchSchedule';
import { ChevronDownIcon, CloseIcon } from '../../app/icons';
import { ContactStateSelect } from '../ContactStateSelect';
import { SignalPicker } from '../SignalPicker';

/** The PATCH response fields this panel reads (same shape as AlarmDetail's). */
interface PatchResponse {
  readonly delivery?: { readonly state?: DeliveryState };
}

export interface InlineAlarmEditorProps {
  boatId: string;
  /** The row this panel expanded from — its id, title and enabled state. */
  view: ConfiguredAlarmView;
  /** The stored definition: what the drafts are read back from and diffed against. */
  def: AlarmDefView;
  /** The signal catalog by path — store units, device names, signal kinds. */
  catalogByPath: ReadonlyMap<string, Signal>;
  /**
   * A path's current observation. Wire it exactly as the page already does:
   * `observationLookup({ live: liveSamples, catalog: catalogByPath })`.
   */
  observationFor: (path: string) => LiveObservation;
  /** Mirrored vessel type — it changes the watch preset WORDS, never the modes. */
  vesselType?: VesselType | null;
  /** Presence; the footer caption says when the change reaches the boat. */
  boatOnline: boolean;
  /** `describeBoatSync(response.boatSync)` — the standing sync fact, or null. */
  boatSyncNote?: string | null;
  /** The page clock, so freshness wording never disagrees with the row above. */
  now: number;
  /** Collapse the panel, changing nothing. */
  onClose: () => void;
  /**
   * A save landed. The argument is the DELIVERY sentence when the boat has not
   * got the change yet, else `null`. The page refreshes the snapshot and owns
   * where that sentence is shown — this panel is closing.
   */
  onSaved: (deliveryNote: string | null) => void;
}

// ---------------------------------------------------------------------------
// Field primitives — native selects and numbers shared by both editor arms
// ---------------------------------------------------------------------------

/**
 * A dropdown field. A real `<select>`: it is
 * keyboard- and screen-reader-native, and the caret is drawn beside it rather
 * than replacing the control with a div that would have to reimplement all of
 * it.
 */
function SlotSelect<V extends string>({
  value,
  options,
  onChange,
  label,
  testId,
}: {
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
  label: string;
  testId: string;
}) {
  return (
    <span className="al-slot">
      <select
        className="al-slot__select"
        value={value}
        aria-label={label}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value as V)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="al-slot__caret" />
    </span>
  );
}

/**
 * The source field, using the full editor's option list.
 *
 * Its own component rather than a `SlotSelect` with fancier options because it
 * carries two things a plain slot has not got: the leading `''` entry (a real
 * "Automatic" choice on most recipes, a REFUSABLE placeholder on a pin-mandatory
 * one), and the two optgroups that sort the likely instances first without ever
 * hiding the rest of the family.
 */
function BindingSlotSelect({
  slot,
  onChange,
  testId,
}: {
  slot: BindingSlot;
  onChange: (path: string) => void;
  testId: string;
}) {
  return (
    // The field is capped rather than left to size itself: a native select takes
    // the width of its WIDEST option, and "Port engine coolant temperature
    // (Yanmar 4JH110)" would push the form beyond the panel.
    <span className="al-slot al-slot--binding">
      <select
        className="al-slot__select"
        value={slot.selected}
        aria-label={`Source — ${slot.label}`}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={slot.placeholder.value}>{slot.placeholder.label}</option>
        {/* One unlabelled group ⇒ a flat list; otherwise every group is named
            and rendered, the likely instances first and the rest SECOND —
            never absent (ADR 0157, and `bindingChoices`'s own rule). */}
        {slot.groups.length === 1 && slot.groups[0]?.label.length === 0
          ? slot.groups[0].options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          : slot.groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
      </select>
      <ChevronDownIcon className="al-slot__caret" />
    </span>
  );
}

/** A number slot: the design's field, with the unit beside it in muted type. */
function NumberSlot({
  value,
  onChange,
  unit,
  label,
  testId,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  unit?: string | null;
  label: string;
  testId: string;
  placeholder?: string;
}) {
  return (
    <span className="al-numslot">
      <input
        className="al-numslot__input"
        type="text"
        inputMode="decimal"
        value={value}
        aria-label={label}
        data-testid={testId}
        {...(placeholder !== undefined ? { placeholder } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
      {unit !== null && unit !== undefined && unit.trim().length > 0 ? (
        <span className="al-unit">{unit}</span>
      ) : null}
    </span>
  );
}

/** The three-way live chip, shared by the trigger row and every clause. */
function VerdictPill({ verdict }: { verdict: boolean | null }) {
  const chip = clauseChip(verdict);
  return (
    <span className={`al-verdict al-verdict--${chip.tone}`} title={chip.detail}>
      {chip.text}
    </span>
  );
}

/** The source reading carries its reported age without claiming it is live. */
function LiveProof({ text }: { text: string }) {
  return (
    <span className="al-proof">
      <strong>{text}</strong>
    </span>
  );
}

/**
 * A link out of the panel that would drop a dirty draft — "Open full editor",
 * "+ add condition". Clean, it is the plain Link it has always been. Dirty, the
 * first press asks, and the two answers are the only honest moves: discard and
 * go, or stay. The half-typed draft cannot travel to the destination page, so
 * anything quieter than a question is silent data loss.
 */
function GuardedEditorLink({
  to,
  label,
  dirty,
  className,
  testId,
}: {
  to: string;
  label: string;
  dirty: boolean;
  className: string;
  testId: string;
}) {
  const [confirming, setConfirming] = useState(false);
  if (!dirty) {
    return (
      <Link className={className} to={to} data-testid={testId}>
        {label}
      </Link>
    );
  }
  if (!confirming) {
    return (
      <button
        type="button"
        className={className}
        data-testid={testId}
        onClick={() => setConfirming(true)}
      >
        {label}
      </button>
    );
  }
  return (
    <span className="al-editor__confirm al-editor__guard" data-testid={`${testId}-guard`}>
      <span className="al-editor__confirm-text">{UNSAVED_GUARD_MESSAGE}</span>
      <Link className="al-textbtn al-editor__danger" to={to}>
        {UNSAVED_GUARD_DISCARD}
      </Link>
      <button type="button" className="al-textbtn" onClick={() => setConfirming(false)}>
        {UNSAVED_GUARD_STAY}
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// The shared shell: footer, error slot, can't-edit-here note
// ---------------------------------------------------------------------------

/**
 * The one delete flow every arm shares — the confirm, the request, and what a
 * 404 or a failure means. `onDeleted` gets the kept-alarm sentence, or null
 * when the alarm went (or was already gone).
 */
function useAlarmDelete(boatId: string, alarmId: string, onDeleted: (note: string | null) => void) {
  const [state, setState] = useState<InlineDeleteState>(INLINE_DELETE_IDLE);
  const removing = isDeleting(state);
  async function remove(): Promise<void> {
    if (removing) return;
    setState(deleting());
    try {
      const res = await api<AlarmRemovalResponse>(`/v1/boats/${boatId}/alerts/${alarmId}`, {
        method: 'DELETE',
        idempotencyKey: crypto.randomUUID(),
      });
      // No navigation and no optimistic removal: the caller re-reads the
      // snapshot, and the row goes with it — or stays, turned off, when the
      // boat had not taken that yet (ADR 0213), with the sentence under it.
      onDeleted(describeRemoval(res));
    } catch (err) {
      // Already gone (another tab, a double tap): the re-read shows the truth.
      if ((err as { status?: number }).status === 404) onDeleted(null);
      else setState(deleteFailed());
    }
  }
  return {
    state,
    removing,
    ask: () => setState(askToDelete()),
    cancel: () => setState(cancelDelete()),
    remove,
  };
}

type AlarmDelete = ReturnType<typeof useAlarmDelete>;

/** The quiet "Delete this alarm" text button; hidden while its confirm is open. */
function DeleteButton({
  alarmId,
  del,
  disabled,
}: {
  alarmId: string;
  del: AlarmDelete;
  disabled: boolean;
}) {
  if (isDeleteConfirming(del.state)) return null;
  return (
    <button
      type="button"
      className="al-textbtn al-editor__danger"
      disabled={disabled}
      data-testid={`alarm-inline-delete-${alarmId}`}
      onClick={del.ask}
    >
      {DELETE_LABEL}
    </button>
  );
}

/** The delete confirm: what it costs, the confirming button, Keep it, and its own error slot. */
function DeleteConfirm({ alarmId, del }: { alarmId: string; del: AlarmDelete }) {
  if (!isDeleteConfirming(del.state)) return null;
  return (
    <div className="al-editor__confirm">
      <p className="al-editor__confirm-text">{DELETE_PROMPT}</p>
      {/* The confirming button carries the confirm's testid: it is the
          element a test has to find, click and read the busy label off. */}
      <button
        type="button"
        className="al-btn al-btn--danger"
        disabled={del.removing}
        data-testid={`alarm-inline-delete-confirm-${alarmId}`}
        onClick={() => void del.remove()}
      >
        {deleteButtonLabel(del.state)}
      </button>
      <button
        type="button"
        className="al-btn al-btn--secondary"
        disabled={del.removing}
        onClick={del.cancel}
      >
        {DELETE_KEEP_LABEL}
      </button>
      {/* Its own slot, not the save error's: the two are about different
          requests, and a failed removal must leave the confirm standing —
          a panel that snapped shut would read as "it was removed". */}
      {deleteErrorText(del.state) !== null ? (
        <p
          className="al-editor__error al-editor__confirm-err"
          role="alert"
          data-testid={`alarm-inline-delete-error-${alarmId}`}
        >
          {deleteErrorText(del.state)}
        </p>
      ) : null}
    </div>
  );
}

function EditorFooter({
  alarmId,
  boatId,
  state,
  blocked,
  caption,
  dirty,
  onSave,
  onCancel,
  onDeleted,
}: {
  alarmId: string;
  boatId: string;
  state: InlineSaveState;
  /** The first validation problem, or null. Blocks the save and is shown. */
  blocked: string | null;
  caption: string;
  /** Unsaved edits exist — the full-editor link asks before dropping them. */
  dirty: boolean;
  onSave: () => void;
  onCancel: () => void;
  /**
   * The DELETE landed: re-read the snapshot and close. `note` is null when the
   * alarm was removed (its row goes with the re-read), or the sentence for an
   * alarm the cloud kept, turned off, because the boat had not taken that yet.
   */
  onDeleted: (note: string | null) => void;
}) {
  const error = saveErrorText(state) ?? blocked;
  // Delete lives in the footer rather than in either arm: a custom alarm and a
  // recipe one are removed the same way (`useAlarmDelete`).
  const del = useAlarmDelete(boatId, alarmId, onDeleted);
  const removing = del.removing;

  return (
    <>
      {error !== null ? (
        <p className="al-editor__error" role="alert" data-testid={`alarm-inline-error-${alarmId}`}>
          {error}
        </p>
      ) : null}
      <div className="al-editor__footer">
        <div className="al-editor__actions">
          <button
            type="button"
            className="al-btn al-btn--primary"
            disabled={isSaving(state) || removing || blocked !== null}
            data-testid={`alarm-inline-save-${alarmId}`}
            onClick={onSave}
          >
            {saveButtonLabel(state)}
          </button>
          <button
            type="button"
            className="al-btn al-btn--secondary"
            disabled={removing}
            data-testid={`alarm-inline-cancel-${alarmId}`}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        <p className="al-editor__caption">{caption}</p>
        {/* What this panel deliberately leaves to the page: unlock-to-custom,
            this alarm's history, duplicate-for-a-sibling.
            Quiet, and always present — but never a silent draft-dropper. */}
        <div className="al-editor__secondary">
          <GuardedEditorLink
            to={fullEditorPath(boatId, alarmId)}
            label="Open full editor"
            dirty={dirty}
            className="al-textbtn al-editor__full"
            testId={`alarm-inline-full-${alarmId}`}
          />
          {/* The far end of the row, and only ever a text button: removing an
            alarm is rare and irreversible, so it gets the quietest affordance
            on the panel and a confirm that says what it costs. */}
          <DeleteButton alarmId={alarmId} del={del} disabled={isSaving(state)} />
        </div>
        <DeleteConfirm alarmId={alarmId} del={del} />
      </div>
    </>
  );
}

/** The honest no-editor state: say why, and where the whole of it lives. */
function CannotEditHere({
  alarmId,
  boatId,
  reason,
}: {
  alarmId: string;
  boatId: string;
  reason: string;
}) {
  return (
    <div className="al-editor__note" data-testid={`alarm-inline-unavailable-${alarmId}`}>
      <p>{reason}</p>
      <Link className="al-textbtn" to={fullEditorPath(boatId, alarmId)}>
        Open full editor
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export function InlineAlarmEditor(props: InlineAlarmEditorProps) {
  const variant = inlineEditorVariant(props.def);
  return (
    <div className="al-editor" data-testid={`alarm-inline-editor-${props.view.id}`}>
      {variant === 'custom' ? (
        <CustomArm {...props} />
      ) : variant === 'withdrawn' ? (
        <WithdrawnArm {...props} />
      ) : (
        <RecipeArm {...props} />
      )}
    </div>
  );
}

/**
 * An alarm on a WITHDRAWN recipe (core ADR-216, TOM-136). No form: nothing can
 * arm it, the boat retires it, and the server refuses an edit that keeps it on.
 * The panel says so in the registry's own words and offers only the two honest
 * exits — Turn off (while it is on) and Delete, behind the usual confirm.
 */
function WithdrawnArm({ boatId, view, def, onClose, onSaved }: InlineAlarmEditorProps) {
  const [turningOff, setTurningOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const del = useAlarmDelete(boatId, view.id, (note) => {
    onSaved(note);
    onClose();
  });
  const removing = del.removing;

  async function turnOff(): Promise<void> {
    if (turningOff || removing) return;
    setTurningOff(true);
    setError(null);
    try {
      onSaved(describeDelivery(await turnOffAlarm(boatId, view.id)));
      onClose();
    } catch (err) {
      setError(recipePatchErrorMessage(err));
      setTurningOff(false);
    }
  }

  return (
    <>
      <div className="al-editor__note" data-testid={`alarm-inline-withdrawn-${view.id}`}>
        <p>{withdrawnAlarmNote(def.recipeId, def.enabled)}</p>
      </div>
      {error !== null ? (
        <p className="al-editor__error" role="alert" data-testid={`alarm-inline-error-${view.id}`}>
          {error}
        </p>
      ) : null}
      <div className="al-editor__footer">
        <div className="al-editor__actions">
          {/* Hidden while the delete confirm is open, so the two cannot race. */}
          {def.enabled && !isDeleteConfirming(del.state) ? (
            <button
              type="button"
              className="al-btn al-btn--primary"
              disabled={turningOff || removing}
              data-testid={`alarm-inline-turn-off-${view.id}`}
              onClick={() => void turnOff()}
            >
              {turningOff ? 'Turning off…' : 'Turn off'}
            </button>
          ) : null}
          <button
            type="button"
            className="al-btn al-btn--secondary"
            disabled={turningOff || removing}
            data-testid={`alarm-inline-cancel-${view.id}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="al-editor__secondary">
          <DeleteButton alarmId={view.id} del={del} disabled={turningOff} />
        </div>
        <DeleteConfirm alarmId={view.id} del={del} />
      </div>
    </>
  );
}

/** Keep the shared field selection; prose segments do not become form fields. */
function renderNumericFields(
  segments: readonly SentenceSegment[],
  alarmId: string,
  triggerClass: TriggerClass | undefined,
  handlers: {
    onNumber: (field: 'setpoint' | 'clearpoint', value: string) => void;
    onDuration: (
      field: 'setDelaySeconds' | 'clearDelaySeconds' | 'staleSeconds',
      v: string
    ) => void;
  }
): ReactNode[] {
  return segments.map((segment, index) => {
    switch (segment.kind) {
      case 'text':
      case 'subject':
      case 'severity':
      case 'watch':
        return null;
      case 'number': {
        const fieldLabel =
          segment.field === 'setpoint'
            ? triggerClass === 'High'
              ? 'Alarm above'
              : 'Alarm below'
            : triggerClass === 'High'
              ? 'Clear below'
              : 'Clear above';
        return (
          <label className="al-edit-field" key={index}>
            <span className="al-edit-field__label">{fieldLabel}</span>
            <NumberSlot
              value={segment.value}
              unit={segment.unit}
              label={`${fieldLabel} (${segment.label.toLowerCase()})`}
              testId={`alarm-inline-${segment.field}-${alarmId}`}
              {...(segment.optional ? { placeholder: '—' } : {})}
              onChange={(v) => handlers.onNumber(segment.field, v)}
            />
          </label>
        );
      }
      case 'duration':
        return (
          <label className="al-edit-field" key={index}>
            <span className="al-edit-field__label">{segment.label}</span>
            <NumberSlot
              value={segment.value}
              unit={DURATION_UNIT}
              label={segment.label}
              testId={`alarm-inline-${segment.field}-${alarmId}`}
              onChange={(v) => handlers.onDuration(segment.field, v)}
            />
          </label>
        );
    }
  });
}

// --- the CUSTOM arm ---------------------------------------------------------

function CustomArm({
  boatId,
  view,
  def,
  catalogByPath,
  observationFor,
  vesselType,
  boatOnline,
  boatSyncNote,
  now,
  onClose,
  onSaved,
}: InlineAlarmEditorProps) {
  const units = useUnits();
  // The DECLARED store unit of the path this alarm is pinned to. Needed beside
  // `units` or nothing converts: the account says fathoms, and only the catalog
  // says the boat is reporting metres. Passing the settings without it is the
  // quiet failure `CustomUnitContext` warns about — it looks exactly like a
  // metric account.
  const customStoreUnit = useMemo(() => {
    const path = readCustomSpec(def)?.path;
    return path === undefined ? undefined : (catalogByPath.get(path)?.unit ?? null);
  }, [def, catalogByPath]);
  // The stored definition read back into the builder's draft. `null` means this
  // build cannot represent it (a clause shape it does not know, more conditions
  // than the form authors) — then NOTHING is offered here, because saving a
  // draft that silently lost a clause would remove a gate nobody touched.
  //
  // With the context, the value boxes read in the owner's unit and each records
  // the baseline that keeps an untouched save bit-identical (ADR 0189 §4).
  const seed = useMemo(
    () =>
      initFromCustomDef(def, {
        units,
        ...(customStoreUnit === undefined ? {} : { storeUnit: customStoreUnit }),
      }),
    [def, units, customStoreUnit]
  );
  // An untouched panel keeps following refreshed definitions through `seed`;
  // the first edit freezes the draft AND captures its baseline (see the module
  // header on why the save must diff against that, never the freshest def).
  const [edit, setEdit] = useState<InlineEdit<CustomAlarmDraft, AlarmDefView> | null>(null);
  const [state, setState] = useState<InlineSaveState>(INLINE_SAVE_IDLE);
  const draft = edit?.draft ?? seed;
  const baseDef = edit?.baseline ?? def;

  const catalogSignals = useMemo(() => [...catalogByPath.values()], [catalogByPath]);
  const labelFor = (path: string): string => {
    const signal = catalogByPath.get(path);
    return signal === undefined ? signalLabel(path) : signalDisplayName(signal);
  };
  // Glyph only, never a rescale: a custom alarm's numbers are typed in the
  // signal's own store unit (`compileCustom` converts nothing), so the store
  // domain IS the owner's vocabulary here (data-mapping trap 1).
  const unitFor = (path: string): string | null =>
    unitVocabulary(catalogByPath.get(path)?.unit).unit;
  const readingFor = (path: string) => observationReading(observationFor(path));

  const panel = useMemo(
    () =>
      draft === null
        ? null
        : livePanel(def, {
            draft,
            ...(draft.signalPath !== undefined
              ? {
                  triggerLabel: labelFor(draft.signalPath),
                  triggerUnit: catalogByPath.get(draft.signalPath)?.unit ?? null,
                }
              : {}),
            labelFor,
            unitFor: (path: string) => catalogByPath.get(path)?.unit,
            // The TRIGGER row only — the clause rows below stay in store units,
            // beside clause thresholds that are typed in them.
            units,
            observationFor,
            now,
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, def, catalogByPath, observationFor, units, now]
  );

  if (draft === null) {
    return (
      <CannotEditHere
        alarmId={view.id}
        boatId={boatId}
        reason="This alarm was set up by a newer version of Neomaris, or carries something this panel can’t show. Nothing is editable here — open the full editor to see all of it."
      />
    );
  }

  // Field edits keep the keys; removing a condition splices them in the same
  // update, so a draft row and its original stored index can never drift.
  const conditionKeys = edit?.conditionKeys ?? initialConditionKeys(draft.conditions.length);
  const apply = (next: CustomAlarmDraft): void => {
    setEdit((prev) =>
      applyInlineEdit(
        prev,
        def,
        next,
        prev?.conditionKeys ?? initialConditionKeys(draft.conditions.length)
      )
    );
    setState(INLINE_SAVE_IDLE);
  };
  const applyRemoveCondition = (index: number): void => {
    setEdit((prev) => {
      const current = prev?.draft ?? draft;
      const keys = prev?.conditionKeys ?? initialConditionKeys(current.conditions.length);
      return applyInlineEdit(
        prev,
        def,
        removeCondition(current, index),
        conditionKeysAfterRemove(keys, index)
      );
    });
    setState(INLINE_SAVE_IDLE);
  };
  // Delete reuses the SAVE path's refresh: `onSaved` says the page has
  // something to re-read, then the panel closes. A removed alarm's row goes with
  // the re-read (`note` null); a kept one stays, turned off, with the note under
  // it, in the slot a save's delivery sentence uses.
  const onDeleted = (note: string | null): void => {
    onSaved(note);
    onClose();
  };

  const watchOptions = watchSlotOptions(draft.enabledModes, vesselType);
  const subject = draft.signalPath === undefined ? view.title : labelFor(draft.signalPath);
  const source = draft.signalPath === undefined ? undefined : catalogByPath.get(draft.signalPath);
  const triggerChoices =
    source === undefined ? [] : suggestedTriggerClasses(source.kind, source.path);
  // The chip beside the set-point BOX, so it names the unit the box is actually
  // in. `customValueUnits` is the one resolver that decides that — the account's
  // unit where a conversion applies, the signal's own store unit otherwise —
  // and the number in the box came out of it, so the label has to as well. The
  // glyph-only `unitFor` is still right for the CLAUSE boxes below, which are
  // typed in store units.
  const boxUnits = customValueUnits(draft);
  const triggerUnit =
    draft.signalPath === undefined
      ? null
      : boxUnits.converted
        ? boxUnits.unitSuffix
        : unitFor(draft.signalPath);
  const segments = sentenceSegments(sentenceInputFromDraft(draft, { subject, unit: triggerUnit }));
  const numericFields = renderNumericFields(segments, view.id, draft.triggerClass, {
    onNumber: (field, value) =>
      apply(field === 'setpoint' ? setSetpoint(draft, value) : setClearpoint(draft, value)),
    onDuration: (field, value) =>
      apply(
        field === 'setDelaySeconds'
          ? setSetDelaySeconds(draft, value)
          : field === 'clearDelaySeconds'
            ? setClearDelaySeconds(draft, value)
            : setStaleSeconds(draft, value)
      ),
  });

  // The trigger's live proof, judged against the EDITED draft.
  const triggerRow = panel?.rows.find((row) => row.kind === 'trigger') ?? null;
  // Each condition row derives its own proof from its own path (below, in the
  // map) — never from a stored-vs-draft zip, which hands a removed row's
  // reading to its neighbour. The in-force gate is the compiler's verdict on
  // the BASELINE definition, looked up at this row's original stored index.
  const setupRows = alarmConditionsSetup(baseDef, {
    unitFor: (p: string) => catalogByPath.get(p)?.unit,
  })?.rows;
  const combined = combinedWouldFire(draft, readingFor, now);

  // Validation runs only against a catalog we actually have: with an empty one
  // every pin reads as "not reported", and blocking a save on a failed catalog
  // fetch would be this panel inventing a problem.
  const errors = catalogSignals.length > 0 ? validateCustom(draft, catalogSignals) : [];
  const blocked = customHasErrors(errors) ? (errors[0]?.message ?? 'Check the values.') : null;
  const errorField = saveErrorField(state);

  async function save(): Promise<void> {
    if (draft === null || isSaving(state)) return;
    // Diff against the BASELINE, never the live prop: a refreshed `def` under a
    // dirty draft would put every remotely-changed, locally-untouched field
    // into the patch with its stale value (see the module header).
    const patch = toCustomPatchBody(draft, baseDef);
    // Nothing changed: never leave the browser. A no-op PATCH still bumps the
    // boat's ruleset version.
    if (isEmptyCustomPatch(patch)) {
      onClose();
      return;
    }
    setState(inlineSaving());
    try {
      const res = await api<PatchResponse>(`/v1/boats/${boatId}/alerts/${view.id}`, {
        method: 'PATCH',
        body: patch,
        idempotencyKey: crypto.randomUUID(),
      });
      const landed = inlineSaved(res.delivery?.state);
      setState(landed);
      onSaved(landed.kind === 'saved' ? landed.note : null);
      onClose();
    } catch (err) {
      // The context is what lets a 422 naming one path land on the condition
      // row that pinned it rather than always on the trigger.
      const mapped = mapPatchError(err, errorContextFor(draft));
      setState(inlineSaveError(mapped.message, mapped.field));
    }
  }

  return (
    <>
      <div className="al-edit-fields">
        <div className="al-edit-field al-edit-field--wide">
          <SignalPicker
            signals={catalogSignals}
            value={draft.signalPath}
            onPick={(picked) => apply(setSignal(draft, picked))}
            label="Source"
            testidPrefix={`alarm-inline-source-${view.id}`}
          />
          {triggerRow !== null ? (
            <p className="al-editor__proof">
              <LiveProof text={triggerRow.valueText} />
            </p>
          ) : null}
        </div>
        <label className="al-edit-field">
          <span className="al-edit-field__label">Severity</span>
          <SlotSelect
            value={draft.severity}
            options={severityOptions()}
            onChange={(severity: AlarmSeverity) => apply(setCustomSeverity(draft, severity))}
            label="Severity"
            testId={`alarm-inline-severity-${view.id}`}
          />
        </label>
        <label className="al-edit-field">
          <span className="al-edit-field__label">Watching</span>
          <SlotSelect
            value={watchOptions.selected}
            options={watchOptions.options}
            onChange={(preset: WatchPresetId) => {
              if (isInertWatchPreset(preset)) return;
              apply(setCustomModes(draft, watchPresetModes(preset)));
            }}
            label="Watching — when to watch"
            testId={`alarm-inline-watch-${view.id}`}
          />
        </label>
        {/* A source change can clear an incompatible trigger. Keep that choice
            next to the values the new trigger will make editable. */}
        {draft.triggerClass === undefined ? (
          <label className="al-edit-field al-edit-field--wide">
            <span className="al-edit-field__label">Trigger</span>
            <SlotSelect<TriggerClass | ''>
              value=""
              options={[
                { value: '', label: 'Choose a trigger…' },
                ...triggerChoices.map((value) => ({ value, label: triggerLabel(value) })),
              ]}
              onChange={(value) => {
                if (value !== '') apply(setTriggerClass(draft, value));
              }}
              label="What sets it off"
              testId={`alarm-inline-trigger-${view.id}`}
            />
          </label>
        ) : draft.triggerClass === 'On' || draft.triggerClass === 'Off' ? (
          <div className="al-edit-field">
            <span className="al-edit-field__label">Trigger</span>
            <span className="al-edit-field__value">{triggerLabel(draft.triggerClass)}</span>
          </div>
        ) : null}
        {numericFields}
      </div>

      {draft.conditions.length > 0 ? (
        <>
          <p className="al-stack__label">Only while</p>
          <div className="al-stack" data-testid={`alarm-inline-conditions-${view.id}`}>
            {draft.conditions.map((row, index) => {
              const path = row.signalPath;
              const signal = path === undefined ? undefined : catalogByPath.get(path);
              const operators = conditionOperatorsFor(signal?.kind ?? '');
              // A clause the COMPILER did not attach is shown, not judged — the
              // same `unbound` decision the read-only conditions block above
              // the row uses (plan D2), read off the BASELINE definition at
              // this row's ORIGINAL index. A length mismatch degrades to "in
              // force", which is what the definition literally says.
              const inForce = setupRows?.[conditionKeys[index] ?? index]?.unbound !== true;
              const verdict = inForce ? clauseHolds(row, readingFor(path ?? ''), now) : null;
              const unit = path === undefined ? null : unitFor(path);
              // This row's own live reading — the exact words livePanel's
              // clause rows carry, derived from the draft row so a removal
              // above cannot hand it a neighbour's proof.
              const proofText =
                path === undefined ? null : observationText(observationFor(path), unit, now);
              return (
                <div className="al-clause" key={conditionKeys[index] ?? index}>
                  {/* A server rejection naming this row's path MARKS the row
                      (uniform border, never a left-edge stripe) — the sentence
                      itself stays in the panel's one error slot, so the same
                      words are not printed twice. */}
                  <div
                    className={
                      'al-clause__row' +
                      (errorField === `condition:${index}` ? ' al-clause__row--err' : '')
                    }
                  >
                    {/* Read-only in phase 2: changing WHICH signal a condition
                        watches needs the signal picker, and that is the full
                        editor's job. */}
                    <span className="al-clause__device">
                      {path === undefined ? 'a signal' : labelFor(path)}
                    </span>
                    <SlotSelect
                      value={row.operator ?? operators[0] ?? 'above'}
                      options={(operators.length > 0
                        ? operators
                        : row.operator !== undefined
                          ? [row.operator]
                          : []
                      ).map((op) => ({ value: op, label: conditionOperatorLabel(op) }))}
                      onChange={(op: ConditionOperator) =>
                        apply(setConditionOperator(draft, index, op))
                      }
                      label={`Condition ${index + 1} test`}
                      testId={`alarm-inline-condition-op-${view.id}-${index}`}
                    />
                    {row.operator === 'above' || row.operator === 'below' ? (
                      <NumberSlot
                        value={row.value ?? ''}
                        unit={unit}
                        label={`Condition ${index + 1} value`}
                        testId={`alarm-inline-condition-value-${view.id}-${index}`}
                        onChange={(v) => apply(setConditionValue(draft, index, v))}
                      />
                    ) : null}
                    <span className="al-clause__spacer" />
                    {proofText !== null ? <LiveProof text={proofText} /> : null}
                    <VerdictPill verdict={verdict} />
                    <button
                      type="button"
                      className="al-clause__remove"
                      aria-label={`Remove condition ${index + 1}`}
                      data-testid={`alarm-inline-condition-remove-${view.id}-${index}`}
                      onClick={() => applyRemoveCondition(index)}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  {/* The joint. ONE logic covers the whole set, so every joint
                      shows the same word and flipping any of them flips them
                      all — the API has no way to say "A and B or C", and two
                      different words on screen would promise one. */}
                  {index < draft.conditions.length - 1 ? (
                    <div className="al-joint__line">
                      <button
                        type="button"
                        className="al-joint"
                        title={jointExplanation(draft.conditionLogic)}
                        data-testid={`alarm-inline-joint-${view.id}-${index}`}
                        onClick={() =>
                          apply(setConditionLogic(draft, toggledLogic(draft.conditionLogic)))
                        }
                      >
                        {jointLabel(draft.conditionLogic)}
                        <ChevronDownIcon className="al-slot__caret" />
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
            <div className="al-stack__foot">
              {/* Adding a condition needs the signal picker — the full editor
                  has it, and half of it here would be worse than a link. Dirty,
                  it asks before dropping the draft. */}
              <GuardedEditorLink
                to={fullEditorPath(boatId, view.id)}
                label="+ add condition"
                dirty={edit !== null}
                className="al-textbtn"
                testId={`alarm-inline-addcond-${view.id}`}
              />
              <span className="al-stack__combined">
                combined:{' '}
                <span
                  className={`al-verdict al-verdict--${combinedVerdict(combined).tone}`}
                  title={combinedVerdict(combined).detail}
                >
                  {combinedVerdict(combined).text}
                </span>
              </span>
            </div>
          </div>
        </>
      ) : null}

      <EditorFooter
        alarmId={view.id}
        boatId={boatId}
        state={state}
        blocked={blocked}
        caption={inlineSyncCaption({ boatOnline, boatSyncNote })}
        dirty={edit !== null}
        onSave={() => void save()}
        onCancel={onClose}
        onDeleted={onDeleted}
      />
    </>
  );
}

// --- the RECIPE arm ---------------------------------------------------------

function RecipeArm({
  boatId,
  view,
  def,
  catalogByPath,
  observationFor,
  vesselType,
  boatOnline,
  boatSyncNote,
  now,
  onClose,
  onSaved,
}: InlineAlarmEditorProps) {
  const templatesState = useAlertTemplates();
  // The account's display units (ADR 0189). They ride the DRAFT — every box, its
  // unit chip and its bounds are resolved from `draft.units`, and the baseline
  // `initFromDef` captures beside each box is what keeps an untouched save
  // bit-identical.
  const units = useUnits();
  const template = useMemo(
    () =>
      templatesState.kind === 'ready'
        ? (findTemplate(templatesState.templates, def.recipeId) ?? null)
        : null,
    [templatesState, def.recipeId]
  );
  const seed = useMemo(
    () => (template === null ? null : initFromDef(def, template, units)),
    [def, template, units]
  );
  // Same first-edit baseline capture as the custom arm (no condition keys —
  // the recipe arm's clause list is read-only).
  const [edit, setEdit] = useState<InlineEdit<AlarmDraft, AlarmDefView> | null>(null);
  const [state, setState] = useState<InlineSaveState>(INLINE_SAVE_IDLE);
  const draft = edit?.draft ?? seed;
  const baseDef = edit?.baseline ?? def;

  const path = recipeSourcePath(draft, baseDef);
  const wireTrigger = readWireTrigger(def);
  const labelFor = (p: string): string => {
    const signal = catalogByPath.get(p);
    return signal === undefined ? signalLabel(p) : signalDisplayName(signal);
  };
  // The instance slot's option list comes from the SAME accepted-path catalog
  // the compiler binds against, so it can only ever offer a path that compiles.
  const catalogSignals = useMemo(() => [...catalogByPath.values()], [catalogByPath]);
  const panel = useMemo(
    () =>
      livePanel(def, {
        draft: null,
        ...(path !== null ? { recipeTrigger: { path, trigger: wireTrigger } } : {}),
        labelFor,
        unitFor: (p: string) => catalogByPath.get(p)?.unit,
        // The TRIGGER row only — `livePanel` keeps its clause rows in store
        // units on purpose, so a clause's reading and its threshold stay in one
        // unit. The trigger row sits directly under the threshold boxes below,
        // which the same account settings filled.
        units,
        observationFor,
        now,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, path, wireTrigger, catalogByPath, observationFor, units, now]
  );

  if (templatesState.kind === 'loading') {
    return <p className="al-editor__note muted">Loading this alarm’s settings…</p>;
  }
  if (draft === null) {
    return (
      <CannotEditHere
        alarmId={view.id}
        boatId={boatId}
        reason={
          templatesState.kind === 'error'
            ? 'We couldn’t load the alarm library, so there is nothing to edit against here.'
            : 'This alarm came from a recipe this build doesn’t know, so its settings can’t be edited here.'
        }
      />
    );
  }

  const apply = (next: AlarmDraft): void => {
    setEdit((prev) => applyInlineEdit(prev, def, next, []));
    setState(INLINE_SAVE_IDLE);
  };
  // Delete reuses the SAVE path's refresh — see the custom arm's own note.
  const onDeleted = (note: string | null): void => {
    onSaved(note);
    onClose();
  };

  const params = recipeParamSlots(draft);
  // Always offer the source, even when its accepted family has one member:
  // the owner can explicitly pin it rather than relying on Automatic. Missing
  // family metadata or an unavailable catalog still cannot invent choices.
  const instance = bindingSlot(draft, catalogSignals);
  const watchOptions = watchSlotOptions(draft.enabledModes, vesselType);
  const subject = path === null ? view.title : labelFor(path);
  const triggerRow = panel.rows.find((row) => row.kind === 'trigger') ?? null;
  const clauseRows = panel.rows.filter((row) => row.kind === 'condition');

  const errors = validateRecipe(draft, { requireTitle: true });
  const blocked = recipeHasErrors(errors)
    ? (errors.title ??
      Object.values(errors.thresholds)[0] ??
      errors.modes ??
      errors.minRpm ??
      errors.binding ??
      'Check the values.')
    : null;

  async function save(): Promise<void> {
    if (draft === null || isSaving(state)) return;
    // Diff against the BASELINE, never the live prop — same revert hazard as
    // the custom arm, and the per-mode split boxes make it worse: a refreshed
    // def under a dirty draft would ship every remote change back stale.
    const patch = toRecipePatchBody(draft, baseDef);
    if (isEmptyRecipePatch(patch)) {
      onClose();
      return;
    }
    setState(inlineSaving());
    try {
      const res = await api<PatchResponse>(`/v1/boats/${boatId}/alerts/${view.id}`, {
        method: 'PATCH',
        body: patch,
        idempotencyKey: crypto.randomUUID(),
      });
      const landed = inlineSaved(res.delivery?.state);
      setState(landed);
      onSaved(landed.kind === 'saved' ? landed.note : null);
      onClose();
    } catch (err) {
      setState(inlineSaveError(recipePatchErrorMessage(err)));
    }
  }

  return (
    <>
      <div className="al-edit-fields">
        <div className="al-edit-field al-edit-field--wide">
          <span className="al-edit-field__label">Source</span>
          {instance !== null ? (
            <BindingSlotSelect
              slot={instance}
              onChange={(nextPath) => apply(setBinding(draft, nextPath))}
              testId={`alarm-inline-binding-${view.id}`}
            />
          ) : (
            <span className="al-edit-field__value">{subject}</span>
          )}
          {triggerRow !== null ? (
            <p className="al-editor__proof">
              <LiveProof text={triggerRow.valueText} />
            </p>
          ) : null}
          {instance === null ? (
            <p
              className="al-editor__hint"
              data-testid={`alarm-inline-source-unavailable-${view.id}`}
            >
              {draft.bindingFamily === undefined
                ? 'Source selection is unavailable for this alarm recipe.'
                : 'Source choices are not available right now.'}
            </p>
          ) : null}
        </div>
        <label className="al-edit-field">
          <span className="al-edit-field__label">Severity</span>
          <SlotSelect
            value={draft.severity}
            options={severityOptions()}
            onChange={(severity: AlarmSeverity) => apply(setRecipeSeverity(draft, severity))}
            label="Severity"
            testId={`alarm-inline-severity-${view.id}`}
          />
        </label>
        <label className="al-edit-field">
          <span className="al-edit-field__label">Watching</span>
          <SlotSelect
            value={watchOptions.selected}
            options={watchOptions.options}
            onChange={(preset: WatchPresetId) => {
              if (isInertWatchPreset(preset)) return;
              apply(setRecipeModes(draft, watchPresetModes(preset)));
            }}
            label="Watching — when to watch"
            testId={`alarm-inline-watch-${view.id}`}
          />
        </label>
      </div>

      {/* A source the compiler cannot bind needs its compile-pending reason
          visible alongside the editable fields. */}
      {instance?.problem != null ? (
        <p
          className="al-editor__pinnote"
          role="status"
          data-testid={`alarm-inline-binding-problem-${view.id}`}
        >
          {instance.problem}
        </p>
      ) : null}

      {/* The recipe's own settings. A shared param is one labelled field;
          one broken out per mode becomes a labelled grid, a field
          per enabled mode — never one box over per-mode numbers. The split is
          the operator's either way, and collapsing it says what it drops. */}
      {params.length > 0 ? (
        <div className="al-params">
          {params.map((param) => (
            <div className={`al-param${param.split ? ' al-param--split' : ''}`} key={param.key}>
              <div className="al-param__head">
                <p className="al-param__label">{param.label}</p>
                {param.canSplit ? (
                  <label className="al-param__toggle">
                    <input
                      type="checkbox"
                      checked={param.split}
                      aria-label={`Per mode — ${SPLIT_TOGGLE_LABEL.toLowerCase()}`}
                      data-testid={`alarm-inline-split-${view.id}-${param.key}`}
                      onChange={(e) => apply(setSplitByMode(draft, param.key, e.target.checked))}
                    />
                    <span>Per mode</span>
                  </label>
                ) : null}
              </div>
              <div className="al-param__fields">
                {param.fields.map((field) => (
                  <label className="al-param__field" key={field.mode ?? 'shared'}>
                    {param.split ? <span className="al-param__mode">{field.caption}</span> : null}
                    {isContactStateParam(param.key) ? (
                      // A detector contact's polarity: one of two answers, not a
                      // number box (TOM-136).
                      <ContactStateSelect
                        className="al-numslot__input"
                        value={field.value}
                        allowBlank={contactStateClearable(draft)}
                        ariaLabel={field.srLabel}
                        testId={`alarm-inline-param-${view.id}-${param.key}`}
                        onChange={(value) =>
                          apply(setThreshold(draft, writeModeFor(draft, field), param.key, value))
                        }
                      />
                    ) : (
                      <NumberSlot
                        value={field.value}
                        unit={param.unitSuffix}
                        label={field.srLabel}
                        testId={
                          // The shared box keeps the id it shipped with; a
                          // per-mode box adds its mode, so a test can name the
                          // one box it means.
                          field.mode === null
                            ? `alarm-inline-param-${view.id}-${param.key}`
                            : `alarm-inline-param-${view.id}-${param.key}-${field.mode}`
                        }
                        onChange={(value) =>
                          apply(setThreshold(draft, writeModeFor(draft, field), param.key, value))
                        }
                      />
                    )}
                  </label>
                ))}
              </div>
              {/* Unchecking throws numbers away. Which one survives, and which
                  go, said BEFORE the click — after it they are not recoverable. */}
              {param.collapseWarning !== null ? (
                <p
                  className="al-editor__hint"
                  data-testid={`alarm-inline-split-warn-${view.id}-${param.key}`}
                >
                  {param.collapseWarning}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* A recipe alarm's conditions are the engine preset and any clause
          authored elsewhere. Read-only here — the full editor owns them — but
          shown, because a gate nobody can see is a gate nobody remembers. */}
      {clauseRows.length > 0 ? (
        <>
          <p className="al-stack__label">Only while</p>
          <div className="al-stack" data-testid={`alarm-inline-conditions-${view.id}`}>
            {clauseRows.map((row, index) => (
              <div className="al-clause" key={`${row.path ?? 'preset'}-${index}`}>
                <div className="al-clause__row">
                  <span className="al-clause__device">{row.label}</span>
                  {row.expectation !== undefined ? (
                    <span className="al-clause__expect">{row.expectation}</span>
                  ) : null}
                  {row.path === undefined ? (
                    !row.inForce ? (
                      <span className="al-clause__note">{row.chip.detail}</span>
                    ) : null
                  ) : (
                    <>
                      <span className="al-clause__spacer" />
                      <LiveProof text={row.valueText} />
                      <span
                        className={`al-verdict al-verdict--${row.chip.tone}`}
                        title={row.chip.detail}
                      >
                        {row.chip.text}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <EditorFooter
        alarmId={view.id}
        boatId={boatId}
        state={state}
        blocked={blocked}
        caption={inlineSyncCaption({ boatOnline, boatSyncNote })}
        dirty={edit !== null}
        onSave={() => void save()}
        onCancel={onClose}
        onDeleted={onDeleted}
      />
    </>
  );
}
